import type {
  ApiComplexRoundResponse,
  ApiScenarioSnapshot,
  ApiSharePosition,
  ApiSimpleCapTableSnapshot,
  ApiStockSplitPreviewRow,
  BankEntry,
  CapTableSnapshot,
  CaptableFundTransaction,
  ComplexRoundResponse,
  ConvertibleInstrument,
  LedgerTransaction,
  ScenarioSnapshot,
  ShareClass,
  ShareClassSummary,
  ShareLedgerEntry,
  SharePosition,
  SimpleCapTableSnapshot,
  StakeholderHolding,
  StakeholderPosition,
  StockSplitPreviewRow,
  WarrantFairValueBreakdown,
} from '../types/captable';
import {
  toNumber,
  toOptionalNumber,
  toNumberOrNull,
  toNumberRecord,
} from '../utils/number-operations';

function mapShareLedgerEntry(entry: ShareLedgerEntry): ShareLedgerEntry {
  return {
    ...entry,
    shares_delta: Number(entry.shares_delta),
    price_per_share: toOptionalNumber(entry.price_per_share),
    total_amount: toOptionalNumber(entry.total_amount),
  };
}

export function mapBankEntry(raw: BankEntry): BankEntry {
  return { ...raw, amount: toNumber(raw.amount) };
}

export function mapCaptableFundTransaction(
  raw: CaptableFundTransaction
): CaptableFundTransaction {
  return {
    ...raw,
    expected_amount: toNumber(raw.expected_amount),
    confirmed_amount: toNumber(raw.confirmed_amount),
  };
}

export function mapLedgerTransaction(
  txn: LedgerTransaction
): LedgerTransaction {
  return {
    ...txn,
    price_per_share: toOptionalNumber(txn.price_per_share),
    pre_money_valuation: toOptionalNumber(txn.pre_money_valuation),
    post_money_valuation: toOptionalNumber(txn.post_money_valuation),
    total_amount: toOptionalNumber(txn.total_amount),
    asset_amount: toOptionalNumber(txn.asset_amount),
    fx_rate: toOptionalNumber(txn.fx_rate),
    total_shares: toOptionalNumber(txn.total_shares),
    // Deliberately not run through toOptionalNumber: the ratio sides carry
    // more precision than a JS number, and the edit form sends them back
    // unchanged. formatNumber takes the string directly for display.
    split_ratio_numerator: txn.split_ratio_numerator,
    split_ratio_denominator: txn.split_ratio_denominator,
    ledger_entries: (txn.ledger_entries ?? []).map(mapShareLedgerEntry),
  };
}

export function mapStockSplitPreviewRow(
  row: ApiStockSplitPreviewRow
): StockSplitPreviewRow {
  return {
    ...row,
    current_shares: toNumber(row.current_shares),
    resulting_shares: toNumber(row.resulting_shares),
  };
}

export function mapConvertibleInstrument(
  raw: ConvertibleInstrument
): ConvertibleInstrument {
  return {
    ...raw,
    investment_amount: toNumber(raw.investment_amount),
    interest_rate: toOptionalNumber(raw.interest_rate),
    discount_rate: toOptionalNumber(raw.discount_rate),
    valuation_cap: toOptionalNumber(raw.valuation_cap),
    strike_price: toOptionalNumber(raw.strike_price),
    number_of_shares: toOptionalNumber(raw.number_of_shares),
    strike_price_in_current_units: toOptionalNumber(
      raw.strike_price_in_current_units
    ),
    number_of_shares_in_current_units: toOptionalNumber(
      raw.number_of_shares_in_current_units
    ),
    coverage_percentage: toOptionalNumber(raw.coverage_percentage),
    conversion_price: toOptionalNumber(raw.conversion_price),
    conversion_shares: toOptionalNumber(raw.conversion_shares),
    accrued_interest: toOptionalNumber(raw.accrued_interest),
    nav_including_interest: toOptionalNumber(raw.nav_including_interest),
    warrant_fair_value: raw.warrant_fair_value
      ? mapWarrantFairValueBreakdown(raw.warrant_fair_value)
      : undefined,
  };
}

function mapWarrantFairValueBreakdown(
  raw: WarrantFairValueBreakdown
): WarrantFairValueBreakdown {
  return {
    ...raw,
    share_price: toNumber(raw.share_price),
    company_valuation: toOptionalNumber(raw.company_valuation),
    total_shares: toOptionalNumber(raw.total_shares),
    share_class_weight: toNumber(raw.share_class_weight),
    strike_price: toNumber(raw.strike_price),
    number_of_shares: toNumber(raw.number_of_shares),
    fair_value: toNumber(raw.fair_value),
  };
}

export function mapShareClass(raw: ShareClass): ShareClass {
  return {
    ...raw,
    liquidation_preference: toOptionalNumber(raw.liquidation_preference),
    liquidation_preference_multiple: toOptionalNumber(
      raw.liquidation_preference_multiple
    ),
    participation_cap_multiple: toOptionalNumber(
      raw.participation_cap_multiple
    ),
    dividend_rate: toOptionalNumber(raw.dividend_rate),
    strike_price: toOptionalNumber(raw.strike_price),
  };
}

function mapStakeholderPosition(pos: StakeholderPosition): StakeholderPosition {
  return {
    ...pos,
    share_positions: toNumberRecord(
      pos.share_positions as Record<string, unknown>
    ),
    as_converted_positions: toNumberRecord(
      pos.as_converted_positions as Record<string, unknown>
    ),
    total_shares: toNumber(pos.total_shares),
    total_as_converted_shares: toNumber(pos.total_as_converted_shares),
    ownership_percentage: toNumber(pos.ownership_percentage),
    convertible_shares: pos.convertible_shares
      ? toNumberRecord(pos.convertible_shares as Record<string, unknown>)
      : undefined,
    total_convertible_shares: toOptionalNumber(pos.total_convertible_shares),
    cost_basis_by_share_class: pos.cost_basis_by_share_class
      ? toNumberRecord(pos.cost_basis_by_share_class as Record<string, unknown>)
      : undefined,
  };
}

function mapStakeholderHolding(
  holding: StakeholderHolding
): StakeholderHolding {
  return {
    ...holding,
    shares: toNumber(holding.shares),
    as_converted_shares: toNumber(holding.as_converted_shares),
    cost_basis: toNumberOrNull(holding.cost_basis),
  };
}

function mapShareClassSummary(summary: ShareClassSummary): ShareClassSummary {
  return {
    ...summary,
    outstanding_shares: toNumber(summary.outstanding_shares),
    as_converted_shares: toNumber(summary.as_converted_shares),
    convertible_shares: toOptionalNumber(summary.convertible_shares),
    total_shares: toNumber(summary.total_shares),
    percentage_of_total: toNumber(summary.percentage_of_total),
  };
}

export function mapCapTableSnapshot(
  snapshot: CapTableSnapshot
): CapTableSnapshot {
  return {
    ...snapshot,
    stakeholder_positions: snapshot.stakeholder_positions.map(
      mapStakeholderPosition
    ),
    stakeholder_holdings: snapshot.stakeholder_holdings.map(
      mapStakeholderHolding
    ),
    share_class_summaries:
      snapshot.share_class_summaries.map(mapShareClassSummary),
    total_outstanding_shares: toNumber(snapshot.total_outstanding_shares),
    total_as_converted_shares: toNumber(snapshot.total_as_converted_shares),
    total_convertible_shares: toOptionalNumber(
      snapshot.total_convertible_shares
    ),
    total_shares: toNumber(snapshot.total_shares),
    conversion_price: toOptionalNumber(snapshot.conversion_price),
    conversion_valuation: toOptionalNumber(snapshot.conversion_valuation),
    unconverted_instruments: snapshot.unconverted_instruments?.map(
      mapConvertibleInstrument
    ),
    shares_breakdown: toNumberRecord(
      snapshot.shares_breakdown as Record<string, unknown>
    ),
  };
}

function mapSharePosition(raw: ApiSharePosition): SharePosition {
  return {
    ...raw,
    shares: toNumber(raw.shares),
  };
}

function mapSimpleCapTableSnapshot(
  raw: ApiSimpleCapTableSnapshot
): SimpleCapTableSnapshot {
  return {
    ...raw,
    positions: raw.positions.map(mapSharePosition),
    total_shares_by_class: toNumberRecord(
      raw.total_shares_by_class as Record<string, unknown>
    ),
  };
}

export function mapScenarioSnapshot(
  raw: ApiScenarioSnapshot
): ScenarioSnapshot {
  return {
    ...raw,
    pre_round: mapSimpleCapTableSnapshot(raw.pre_round),
    post_round: mapSimpleCapTableSnapshot(raw.post_round),
  };
}

export function mapComplexRoundResponse(
  raw: ApiComplexRoundResponse
): ComplexRoundResponse {
  return {
    ...raw,
    pre_round_snapshot: mapSimpleCapTableSnapshot(raw.pre_round_snapshot),
    post_round_snapshot: mapSimpleCapTableSnapshot(raw.post_round_snapshot),
  };
}
